/**
 * `AccountTable` - semantic `<table>` of `AccountRow`s.
 *
 * Accessibility (per 04a / 04b / 04c):
 *   - Semantic table with `<thead>` + `<tbody>` and `<th scope="col">`.
 *   - Each row receives focus via Tab; Down/Up arrows move focus between
 *     rows; Enter/Space activates; Esc returns focus to the FilterBar via
 *     `onEscape`.
 *   - `aria-busy="true"` while loading; the in-page busy banner is owned
 *     by the surrounding `ListPage` (so the loading skeleton can sit
 *     inside the same `<table>` shell).
 *   - `caption` is screen-reader only - the visible heading lives in the
 *     page chrome.
 *
 * The table is intentionally dumb: it owns no fetch, no URL state, no
 * filter. It receives a flat array of accounts (pages already flattened
 * by `ListPage`) and a set of callbacks. This makes the keyboard
 * contract trivial to unit-test in isolation.
 */

import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import type { Account } from '../lib/bff-client'
import { ACCOUNT_COLUMNS } from '../features/accounts/columns'
import { AccountRow } from '../features/accounts/account-row'

export interface AccountTableProps {
  readonly accounts: readonly Account[]
  readonly busy: boolean
  /** Called when the user presses Esc on a row, to refocus the FilterBar. */
  readonly onEscape: () => void
  /** Children rendered inside `<tbody>` instead of rows; used for
   * skeleton / empty / error states so the table chrome stays consistent. */
  readonly bodyOverride?: React.ReactNode
  /** Rendered inside the card, below the table, above the bottom border.
   * The design handoff puts the row count and pager in the table's own
   * footer rather than in a detached block beneath the card. */
  readonly footer?: React.ReactNode
}

export function AccountTable({
  accounts,
  busy,
  onEscape,
  bodyOverride,
  footer,
}: AccountTableProps): JSX.Element {
  const tbodyRef = useRef<HTMLTableSectionElement | null>(null)
  const navigate = useNavigate()

  const handleArrow = (direction: 1 | -1, currentIndex: number): void => {
    const next = currentIndex + direction
    const body = tbodyRef.current
    if (!body) return
    const target = body.querySelector<HTMLTableRowElement>(`tr[data-row-index="${next}"]`)
    if (target) {
      target.focus()
    }
  }

  const handleActivate = (account: Account): void => {
    navigate(`/members/${account.id}`)
  }

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-border-default bg-bg-surface shadow-elev-1">
      {/*
        The table body scrolls inside the card rather than the whole page
        scrolling. That is what makes `position: sticky` on the header cells
        work: sticky resolves against the nearest scrolling ancestor, so the
        headers have to share a scroll container with the rows.

        The payoff for a 50-row table is that the column headers and the
        pager both stay on screen while you scan - previously the headers
        were gone after about a dozen rows and the pager needed a scroll to
        the bottom of the page to reach.

        `min-h-0` on this element and its flex ancestors is what lets the
        scroll region actually bound itself; without it a flex child refuses
        to shrink below its content height and nothing scrolls.
      */}
      <div className="min-h-0 flex-1 overflow-auto">
        <table
          role="table"
          aria-busy={busy ? 'true' : 'false'}
          aria-label="Members"
          className="w-full border-collapse text-murmuration-body"
        >
          <caption className="sr-only">
            Members. Use arrow keys to move between rows; press Enter to open.
          </caption>
          <thead className="text-text-secondary">
            <tr>
              {ACCOUNT_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-label={col.ariaLabel}
                  // Sticky sits on the cells, not on <thead>: with
                  // `border-collapse` the row's border is not painted with a
                  // sticky <thead> in several browsers, so the header would
                  // scroll under the rows with no separating line.
                  className="sticky top-0 z-10 border-b border-border-default bg-bg-surface-2 px-[12px] py-murmuration-2 text-left text-murmuration-eyebrow uppercase"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {bodyOverride !== undefined ? (
              <tr>
                <td colSpan={ACCOUNT_COLUMNS.length} className="p-0">
                  {bodyOverride}
                </td>
              </tr>
            ) : (
              accounts.map((account, index) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  rowIndex={index}
                  onActivate={handleActivate}
                  onArrow={handleArrow}
                  onEscape={onEscape}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {footer !== undefined && <div className="flex-none">{footer}</div>}
    </div>
  )
}
